import { useMemo } from 'react';
import { ActionIcon } from '@mantine/core';
import { DataTable, CellSort, EmptyRowsFallback } from '@shared/ui';
import { IconPencil } from '@tabler/icons-react';
import { type AIConstant } from '@entities/aiConstants';
import { useAIConstantShowModal } from '../useAIConstantShowModal';

type AIConstantsListProps = {
  aiConstants: AIConstant[];
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: Record<string, 'asc' | 'desc'>;
  isLoading: boolean;
  totalRecords: number;
};

export const AIConstantsList = ({
  aiConstants,
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  onSortChange,
  currentSort,
  isLoading,
  totalRecords,
}: AIConstantsListProps) => {
  const { openAIConstantShowModal } = useAIConstantShowModal();

  const handleEditAIConstant = (record: AIConstant) => {
    openAIConstantShowModal(record.id.toString());
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => onSortChange('id', dir)}
            currentDirection={currentSort.id}
          />
        ),
      },
      {
        accessor: 'code',
        title: 'Курс',
      },
      {
        accessor: 'name',
        title: (
          <CellSort
            label='Наименование константы'
            onSort={dir => onSortChange('name', dir)}
            currentDirection={currentSort.name}
          />
        ),
      },
      {
        accessor: 'value',
        title: (
          <CellSort
            label='Значение константы'
            onSort={dir => onSortChange('value', dir)}
            currentDirection={currentSort.value}
          />
        ),
      },
      {
        accessor: 'custom',
        title: 'Действия',
        render: (record: AIConstant) => (
          <ActionIcon
            size={38}
            variant='transparent'
            onClick={() => handleEditAIConstant(record)}
          >
            <IconPencil color='var(--mantine-color-primary-7)' size={20} />
          </ActionIcon>
        ),
      },
    ],
    [currentSort, onSortChange, handleEditAIConstant]
  );

  return (
    <DataTable<AIConstant>
      fetching={isLoading}
      columns={columns}
      records={aiConstants}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      emptyState={<EmptyRowsFallback desc='Список констант пуст' subDesc='' />}
      noRecordsText='Список констант пуст'
    />
  );
};
